import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Slider, Snackbar, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import api from '../api';
import PracticalAssessment from '../components/PracticalAssessment';
import PracticalInstructions from '../components/PracticalInstructions';
import { calculateRefractiveIndex, checkPinAlignment, criticalAngle, traceRefractionPath, type Point2D } from './physics/refraction';

type RefractionMode = 'pin-method' | 'snells-law' | 'critical-angle' | 'lateral-displacement' | 'parallax-check' | 'sandbox';

interface SavedSession {
  _id: string;
  title: string;
  mode: RefractionMode;
  incidentAngle?: number;
  refractiveIndex?: number;
  thicknessMm?: number;
  pinPositions?: Point2D[];
  eyePosition?: { x: number; y: number };
  observations?: string;
  conclusion?: string;
  updatedAt?: string;
  createdAt?: string;
}

const MODE_LABELS: Record<RefractionMode, string> = {
  'pin-method': 'A — Pin method',
  'snells-law': 'B — Snell’s law',
  'critical-angle': 'C — Critical angle',
  'lateral-displacement': 'D — Lateral displacement',
  'parallax-check': 'E — Parallax check',
  sandbox: 'F — Free exploration',
};

const defaultPins: Point2D[] = [
  { x: 18, y: 18 },
  { x: 28, y: 24 },
  { x: 42, y: 22 },
  { x: 56, y: 18 },
];

const RefractionGlassBlockLab: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openLogin } = useAuthModal();
  const [mode, setMode] = useState<RefractionMode>('pin-method');
  const [incidentAngle, setIncidentAngle] = useState(35);
  const [refractiveIndex, setRefractiveIndex] = useState(1.5);
  const [thicknessMm, setThicknessMm] = useState(60);
  const [pinPositions, setPinPositions] = useState<Point2D[]>(defaultPins);
  const [eyePosition, setEyePosition] = useState({ x: 54, y: 58 });
  const [observations, setObservations] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showIdealRay, setShowIdealRay] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trace = useMemo(() => traceRefractionPath({ incidentAngleDeg: incidentAngle, refractiveIndex, thicknessMm }), [incidentAngle, refractiveIndex, thicknessMm]);
  const alignment = useMemo(() => checkPinAlignment(pinPositions, 0.12), [pinPositions]);
  const criticalTheta = useMemo(() => criticalAngle(refractiveIndex), [refractiveIndex]);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingSessions(true);
    try {
      const response = await api.get('/refraction-sessions');
      const sessions = response.data?.data || [];
      setSavedSessions(sessions);
      if (!sessionId && sessions.length) {
        const existing = sessions[0];
        setSessionId(existing._id);
        setMode(existing.mode || 'pin-method');
        setIncidentAngle(existing.incidentAngle ?? 35);
        setRefractiveIndex(existing.refractiveIndex ?? 1.5);
        setThicknessMm(existing.thicknessMm ?? 60);
        setPinPositions(existing.pinPositions?.length ? existing.pinPositions : defaultPins);
        setEyePosition(existing.eyePosition || { x: 54, y: 58 });
        setObservations(existing.observations || '');
        setConclusion(existing.conclusion || '');
      }
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Saved refraction sessions could not be loaded.');
    } finally {
      setLoadingSessions(false);
    }
  }, [isAuthenticated, sessionId]);

  useEffect(() => { if (!authLoading && isAuthenticated) void loadSessions(); }, [authLoading, isAuthenticated, loadSessions]);

  const persistSession = useCallback(async (nextState = { mode, incidentAngle, refractiveIndex, thicknessMm, pinPositions, eyePosition, observations, conclusion }) => {
    if (!isAuthenticated || !user) return;
    setSaving(true);
    try {
      let activeId = sessionId;
      if (!activeId) {
        const created = await api.post('/refraction-sessions', {
          title: 'Refraction Through Glass Block',
          mode,
          incidentAngle,
          refractiveIndex,
          thicknessMm,
        });
        activeId = created.data?.data?._id;
        if (!activeId) throw new Error('Session id missing from response');
        setSessionId(activeId);
      }
      await api.patch(`/refraction-sessions/${activeId}`, {
        mode: nextState.mode,
        incidentAngle: nextState.incidentAngle,
        refractiveIndex: nextState.refractiveIndex,
        thicknessMm: nextState.thicknessMm,
        pinPositions: nextState.pinPositions,
        eyePosition: nextState.eyePosition,
        observations: nextState.observations,
        conclusion: nextState.conclusion,
      });
      setMessage('Session saved to your account.');
      await loadSessions();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Autosave for this refraction lab is unavailable.');
    } finally {
      setSaving(false);
    }
  }, [isAuthenticated, user, sessionId, mode, incidentAngle, refractiveIndex, thicknessMm, pinPositions, eyePosition, observations, conclusion, loadSessions]);

  useEffect(() => {
    if (!sessionId || !isAuthenticated) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void persistSession(); }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [sessionId, isAuthenticated, mode, incidentAngle, refractiveIndex, thicknessMm, pinPositions, eyePosition, observations, conclusion, persistSession]);

  const handleReset = () => {
    setMode('pin-method');
    setIncidentAngle(35); setRefractiveIndex(1.5); setThicknessMm(60); setPinPositions(defaultPins); setEyePosition({ x: 54, y: 58 });
    setObservations(''); setConclusion(''); setShowIdealRay(false);
    setMessage('Bench reset. Previous saved sessions remain available in your account.');
  };

  const movePin = (index: number, dx: number, dy: number) => {
    setPinPositions((current) => current.map((pin, pinIndex) => pinIndex === index ? { x: Math.min(100, Math.max(0, pin.x + dx)), y: Math.min(100, Math.max(0, pin.y + dy)) } : pin));
  };

  const handleResumeSession = (saved: SavedSession) => {
    setSessionId(saved._id); setMode(saved.mode || 'pin-method'); setIncidentAngle(saved.incidentAngle ?? 35); setRefractiveIndex(saved.refractiveIndex ?? 1.5); setThicknessMm(saved.thicknessMm ?? 60); setPinPositions(saved.pinPositions?.length ? saved.pinPositions : defaultPins); setEyePosition(saved.eyePosition || { x: 54, y: 58 }); setObservations(saved.observations || ''); setConclusion(saved.conclusion || '');
  };

  if (authLoading) return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!isAuthenticated) return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="info" action={<Button color="inherit" onClick={() => openLogin()}>Sign in</Button>}>Sign in to use the Virtual Refraction Laboratory.</Alert></Container>;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f7fbff 0%, #eefaf7 100%)', py: 3 }}>
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Back to labs"><IconButton onClick={() => navigate('/labs')}><ArrowBackIcon /></IconButton></Tooltip>
            <Box>
              <Typography variant="h4" fontWeight={900}>Refraction Through a Glass Block</Typography>
              <Typography color="text.secondary">Use the pin method, measure the ray path, and compare to the ideal refracted trace.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button startIcon={<RestartAltIcon />} color="warning" onClick={handleReset}>Reset</Button>
            <Chip label={saving ? 'Saving…' : sessionId ? 'Autosaved' : 'New session'} color={sessionId ? 'success' : 'default'} />
          </Stack>
        </Stack>

        <PracticalAssessment practicalType="refraction" title={`${MODE_LABELS[mode]} Assessment Question`} questionTag={`refraction-${mode}`} />
        <PracticalInstructions
          title="Refraction through a glass block: practical instructions"
          introduction="Trace how a light ray bends on entering and leaving a rectangular glass block. Use alignment pins to establish the incident and emergent rays, then compare the measured geometry with the model."
          sections={[
            { title: 'A. Pin method and ray tracing', steps: [
              'Place the rectangular glass block on plain paper and trace its outline. Mark where the incident ray meets the first face.',
              'Draw a normal at the entry point, perpendicular to the face. Measure the angle of incidence from the normal, not from the glass surface.',
              'Set the angle of incidence and glass refractive index in the controls. Keep block thickness fixed for one set of readings.',
              'Place pins P1 and P2 upright along the incident direction, separated by several centimetres. View them through the opposite face of the block.',
              'Move the eye position to check for parallax. Adjust the alignment pins until they appear in one straight line with the images of P1 and P2.',
              'Record the pin positions and alignment. Trace the emergent ray through the aligned pins, then join the entry and exit points through the block.',
              'Measure the refracted angle inside the block, the emergent angle outside, and the lateral displacement.'
            ] },
            { title: 'B. Analyse the results', steps: [
              'Use Snell’s law, n = sin(i)/sin(r), with both angles measured from the normal. Compare the result with the selected glass value.',
              'Repeat for several incidence angles while keeping the same block and method. Record incidence, refraction, emergence, and displacement for each trial.',
              'For a rectangular block, check whether the emergent ray is approximately parallel to the incident ray and explain the sideways displacement.',
              'In Critical angle mode, increase incidence for a ray travelling from glass toward air and locate the limiting angle. For glass-to-air, sin(c) = 1/n.',
              'In Lateral displacement mode, compare block thickness and incidence angle. Explain how each changes displacement.',
              'Discuss uncertainty from pin thickness, parallax, angle measurement, and block-edge alignment.'
            ] },
            { title: 'C. Use the simulator', steps: [
              'Choose a mode before collecting results; the selected mode also determines the assessment question above.',
              'Move alignment pins in small steps and change eye position to test whether the pin line is truly aligned.',
              'Reveal the ideal ray only after making your own alignment or prediction, then compare your path with the model.',
              'Record observations and a conclusion. Saved sessions preserve the mode, angles, pin positions, and notes.'
            ] }
          ]}
          safety={[
            'Keep pins vertical and handle their points carefully; do not direct them toward another person.',
            'Keep the glass block flat and do not force it against the bench. Report chipped or broken glass in a real laboratory.',
            'Do not look directly into a bright ray source. The simulation models ray geometry and does not require a real laser.'
          ]}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, gap: 3 }}>
          <Box>
            <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="refraction-mode-label">Experiment mode</InputLabel>
                  <Select labelId="refraction-mode-label" label="Experiment mode" value={mode} onChange={(event) => setMode(event.target.value as RefractionMode)}>
                    {Object.entries(MODE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </Select>
                </FormControl>
                <Paper elevation={0} sx={{ px: 2, py: 1, minWidth: 200, border: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography variant="caption">Angle of incidence</Typography>
                  <Slider value={incidentAngle} min={10} max={80} step={1} onChange={(_, value) => setIncidentAngle(Array.isArray(value) ? value[0] : value)} />
                  <Typography variant="body2">{incidentAngle.toFixed(0)}°</Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 2, py: 1, minWidth: 200, border: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography variant="caption">Refractive index</Typography>
                  <Slider value={refractiveIndex} min={1.1} max={2.2} step={0.01} onChange={(_, value) => setRefractiveIndex(Array.isArray(value) ? value[0] : value)} />
                  <Typography variant="body2">{refractiveIndex.toFixed(2)}</Typography>
                </Paper>
              </Stack>

              <Box sx={{ position: 'relative', height: 440, border: '1px solid rgba(25,118,210,0.18)', borderRadius: 3, overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #edf7ff 100%)' }}>
                <Box component="svg" viewBox="0 0 100 100" sx={{ width: '100%', height: '100%' }}>
                  <rect x="18" y="22" width="40" height="34" rx="2" fill="#d7e8ff" stroke="#6286d8" strokeWidth="0.75" />
                  <line x1="38" y1="22" x2="38" y2="56" stroke="#a0aec0" strokeWidth="0.6" strokeDasharray="2 2" />
                  <line x1="18" y1="38" x2="58" y2="38" stroke="#a0aec0" strokeWidth="0.6" strokeDasharray="2 2" />
                  <line x1="50" y1="22" x2="65" y2="58" stroke="#ff7b54" strokeWidth="0.8" opacity={showIdealRay ? 1 : 0.15} />
                  <line x1="50" y1="22" x2="62" y2="38" stroke="#55b6ff" strokeWidth="0.8" opacity={showIdealRay ? 1 : 0.15} />
                  <circle cx={eyePosition.x} cy={eyePosition.y} r="3" fill="#0a7e66" />
                  {pinPositions.map((pin, index) => (
                    <g key={`pin-${index}`}>
                      <circle cx={pin.x} cy={pin.y} r="2.2" fill={alignment.isAligned ? '#26a69a' : '#f46e6e'} />
                      <circle cx={pin.x} cy={pin.y} r="1.2" fill="#ffffff" />
                    </g>
                  ))}
                  {pinPositions.map((pin, index) => (
                    <foreignObject key={`pin-label-${index}`} x={pin.x + 2} y={pin.y - 4} width="10" height="10">
                      <div style={{ fontSize: '4px', fontWeight: 700, color: '#1f2937' }}>{index + 1}</div>
                    </foreignObject>
                  ))}
                </Box>
                <Box sx={{ position: 'absolute', left: 18, top: 18 }}>
                  <Button size="small" variant="contained" onClick={() => setShowIdealRay((value) => !value)} startIcon={<VisibilityIcon />}>{showIdealRay ? 'Hide ideal ray' : 'Reveal ideal ray'}</Button>
                </Box>
                <Box sx={{ position: 'absolute', right: 18, top: 18, display: 'flex', gap: 1, flexDirection: 'column' }}>
                  <Button size="small" variant="outlined" onClick={() => setEyePosition((current) => ({ ...current, x: 48 }))}>Left eye</Button>
                  <Button size="small" variant="outlined" onClick={() => setEyePosition((current) => ({ ...current, x: 54 }))}>Centre</Button>
                  <Button size="small" variant="outlined" onClick={() => setEyePosition((current) => ({ ...current, x: 62 }))}>Right eye</Button>
                </Box>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                {pinPositions.map((pin, index) => (
                  <Button key={`pin-control-${index}`} size="small" variant="outlined" onClick={() => movePin(index, 3, 0)}>Move pin {index + 1}</Button>
                ))}
              </Stack>
            </Paper>
          </Box>

          <Box>
            <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
              <Typography variant="h6" fontWeight={800}>Live calculations</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Card variant="outlined"><CardContent><Typography variant="caption">Snell’s law value</Typography><Typography>{calculateRefractiveIndex(incidentAngle, trace.refractedAngleDeg).toFixed(3)}</Typography></CardContent></Card>
                <Card variant="outlined"><CardContent><Typography variant="caption">Critical angle</Typography><Typography>{criticalTheta.toFixed(1)}°</Typography></CardContent></Card>
                <Card variant="outlined"><CardContent><Typography variant="caption">Lateral displacement</Typography><Typography>{trace.lateralDisplacementMm.toFixed(2)} mm</Typography></CardContent></Card>
                <Card variant="outlined"><CardContent><Typography variant="caption">Pin alignment</Typography><Typography>{alignment.isAligned ? 'Aligned within tolerance' : `Offset ${alignment.maxDeviation.toFixed(2)} units`}</Typography></CardContent></Card>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
              <Typography variant="h6" fontWeight={800}>Experimental notes</Typography>
              <TextField fullWidth multiline minRows={4} label="Observations" value={observations} onChange={(event) => setObservations(event.target.value)} sx={{ mt: 1.5 }} />
              <TextField fullWidth multiline minRows={3} label="Conclusion" value={conclusion} onChange={(event) => setConclusion(event.target.value)} sx={{ mt: 1.5 }} />
            </Paper>

            <Paper sx={{ p: 2 }} elevation={0}>
              <Typography variant="h6" fontWeight={800}>My saved sessions</Typography>
              {loadingSessions && <CircularProgress size={20} sx={{ my: 1 }} />}
              {!loadingSessions && savedSessions.length === 0 && <Typography variant="body2" color="text.secondary">Your refraction practical sessions will appear here.</Typography>}
              <Stack spacing={1} sx={{ mt: 1 }}>
                {savedSessions.slice(0, 6).map((saved) => (
                  <Button key={saved._id} variant={saved._id === sessionId ? 'contained' : 'outlined'} size="small" onClick={() => handleResumeSession(saved)} sx={{ justifyContent: 'space-between', textTransform: 'none' }}>
                    <span>{saved.title} · {MODE_LABELS[saved.mode] || saved.mode}</span>
                    <span>{new Date(saved.updatedAt || saved.createdAt || Date.now()).toLocaleDateString()}</span>
                  </Button>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Container>
      <Snackbar open={Boolean(message)} autoHideDuration={3000} onClose={() => setMessage('')} message={message} />
      {error && <Alert severity="warning" sx={{ m: 2 }} onClose={() => setError('')}>{error}</Alert>}
    </Box>
  );
};

export default RefractionGlassBlockLab;
